<template>
    <Toast />
    <PaymentRequired v-if="showAlert" :is-bank-provided="isBankProvided" :is-vat-provided="isVatProvided" />

    <div>
        <div class="grid grid-cols-12 gap-8">
            <!-- First row -->
            <div class="col-span-12">
                <div class="flex flex-col sm:flex-row gap-6 sm:items-center sm:justify-between mt-5">
                    <!-- User Info -->
                    <div class="flex flex-col items-center sm:items-start" v-if="user">
                        <span class="text-900 font-bold text-2xl sm:text-4xl text-center sm:text-left">{{ user.name
                            }}</span>
                        <p class="text-600 m-0 text-sm sm:text-base"> Last logged in on {{
                            getFormattedLoggedInDate(user.lastLogin) }}</p>
                    </div>

                    <!-- Middle section for traffic sources and geos -->
                    <div class="flex flex-col items-center sm:items-start gap-4 w-full sm:w-auto"
                        v-if="user && userRole === 'publisher'">
                        <!-- Traffic Sources -->
                        <div class="flex flex-wrap gap-2 w-full max-w-[300px] sm:max-w-none group relative">
                            <div
                                class="flex flex-wrap gap-1 max-h-[64px] overflow-hidden justify-center sm:justify-start w-full">
                                <Tag v-for="source in user.trafficSource.slice(0, 2)" :key="source"
                                    :value="siteTypeMap[source]" severity="info" class="!px-2 !py-1" />
                                <Tag v-if="user.trafficSource.length > 2" :value="`+${user.trafficSource.length - 2}`"
                                    severity="secondary" class="!px-2 !py-1 cursor-help" />
                            </div>
                            <!-- Hover popup -->
                            <div v-if="user.trafficSource.length > 2"
                                class="absolute left-0 sm:left-auto sm:right-0 top-full mt-2 bg-white shadow-lg rounded-md p-2 hidden group-hover:flex flex-wrap gap-1 z-50">
                                <Tag v-for="source in user.trafficSource" :key="source" :value="siteTypeMap[source]"
                                    severity="info" class="!px-2 !py-1" />
                            </div>
                        </div>

                        <!-- Geos -->
                        <div class="flex flex-wrap gap-2 w-full max-w-[300px] sm:max-w-none group relative">
                            <div
                                class="flex flex-wrap gap-1 max-h-[64px] overflow-hidden justify-center sm:justify-start w-full">
                                <Tag v-for="geo in user.trafficGeo.slice(0, 2)" :key="geo" class="!px-2 !py-1">
                                    <span :class="`fi fi-${geo.toLowerCase()}`" class="mr-2"></span>
                                    {{ getCountryName(geo) }}
                                </Tag>
                                <Tag v-if="user.trafficGeo.length > 2" :value="`+${user.trafficGeo.length - 2}`"
                                    severity="secondary" class="!px-2 !py-1 cursor-help" />
                            </div>
                            <!-- Hover popup -->
                            <div v-if="user.trafficGeo.length > 2"
                                class="absolute left-0 sm:left-auto sm:right-0 top-full mt-2 bg-white shadow-lg rounded-md p-2 hidden group-hover:flex flex-wrap gap-1 z-50">
                                <Tag v-for="geo in user.trafficGeo" :key="geo" class="!px-2 !py-1">
                                    <span :class="`fi fi-${geo.toLowerCase()}`" class="mr-2"></span>
                                    {{ getCountryName(geo) }}
                                </Tag>
                            </div>
                        </div>
                    </div>

                    <!-- Status Tag -->
                    <div class="flex flex-col items-center sm:items-end mt-4 sm:mt-0"
                        v-if="user && userRole === 'publisher'">
                        <Tag :value="user.status" :severity="Utils.getUserStatusSeverity(user.status)"
                            style="border-color: #86bcf1;"
                            class="border border-opacity-20 !h-16 sm:!h-20 !w-36 sm:!w-44 !text-lg sm:!text-xl" />
                    </div>
                </div>
            </div>

            <!-- Second row -->
            <!-- details -->
            <div class="col-span-12 md:col-span-6 §xl:col-span-4 ">
                <Card class=" h-full card-bg">
                    <template #content>
                        <div>
                            <p class="m-0" v-if="user.type === 'Company'"><strong>Company:</strong> {{ user.companyName
                            }}</p>
                            <p class="m-0"><strong>Email:</strong> {{ user.email }}</p>
                            <p class="m-0"><strong>Contact Number:</strong> {{ user.contactNumber }}</p>
                            <div class="flex items-center space-x-2">
                                <p class="m-0"><strong>Country:</strong> {{ getCountryName(user.country) }}</p>
                                <span :class="`fi fi-${user.country.toLowerCase()}`" class="mr-2"></span>
                            </div>
                            <p class="m-0"><strong>Created:</strong> {{ user.created }}</p>
                        </div>
                    </template>

                </Card>
            </div>
            <!-- revenue -->
            <!-- <div class="col-span-12 md:col-span-6 xl:col-span-2">
                <Card class="h-full card-bg">
                    <template #title><span class="font-semibold text-lg">Revenue</span> </template>
                    <template #content>
                        <div class="flex flex-1 items-center justify-center">
                            <span class="text-3xl font-bold text-surface-900 text-green-700">
                                <p v-if="totalNetRevenue" class="text-center ">
                                    €{{ totalNetRevenue }}
                                </p>
                                <Skeleton v-else width="50px" height="50px" />
                            </span>
                        </div>
                    </template>
                </Card>
            </div> -->
            <!-- commission -->
            <div v-if="user && user.commission !== 0" class="col-span-12 md:col-span-6 xl:col-span-2">
                <Card class="h-full card-bg">
                    <template #title>
                        <span class="font-semibold text-lg border-b border-gray-300 ">Commission</span>
                    </template>
                    <template #content>
                        <div class="flex flex-1 items-center justify-center">
                            <span class="text-3xl font-bold text-surface-900 dark:text-surface-0">
                                <p v-tooltip.bottom="'This is the commission we take'" class="text-center">
                                    {{ user.commission }}%
                                </p>
                                <Skeleton v-if="!user" width="50px" height="50px" />
                            </span>
                        </div>
                    </template>
                </Card>
            </div>
            <!-- total links -->
            <div class="col-span-12 md:col-span-6 xl:col-span-2" v-if="user && userRole === 'publisher'">
                <Card class="h-full card-bg">
                    <template #title><span class="font-semibold text-lg border-b border-gray-300 "> Total
                            Links</span></template>
                    <template #content>
                        <div class="flex flex-1 items-center justify-center">
                            <span class="text-4xl font-bold text-surface-900 dark:text-surface-0">
                                <Skeleton v-if="userMetaData === null" width="50px" height="50px" />
                                <span v-else>{{ userMetaData.siteCount }}</span>
                            </span>
                        </div>
                    </template>
                </Card>
            </div>
            <!-- total sites -->
            <div class="col-span-12 md:col-span-6 xl:col-span-2" v-if="user && userRole === 'publisher'">
                <Card class="h-full card-bg">
                    <template #title><span class="font-semibold text-lg border-b border-gray-300 ">Total sites</span>
                    </template>
                    <template #content>
                        <div class="flex flex-1 items-center justify-center">
                            <span class="text-4xl font-bold text-surface-900 dark:text-surface-0">
                                <Skeleton v-if="userMetaData === null" width="50px" height="50px" />
                                <span v-else>{{ userMetaData.linkCount }}</span>
                            </span>
                        </div>
                    </template>
                </Card>
            </div>
            <div class="col-span-12 md:col-span-6 xl:col-span-2">
            </div>
            <!-- Third row -->
            <!-- Vat details -->
            <div class="col-span-12 md:col-span-6 xl:col-span-3 md:col-start-1">
                <Card class="h-full card-bg">
                    <template #title>
                        <span class="font-semibold text-lg border-b border-gray-300 ">VAT number</span>
                    </template>
                    <template #content>
                        <div class="w-full">
                            <div id="name-section" class="w-full">
                                <!-- Vat number -->
                                <div class="flex flex-col  w-full">
                                    <div class="flex justify-center items-center ">
                                        <span v-if="isVatProvided">{{ user.vat }}</span>
                                        <InputText v-else class="mt-1" v-model="vatNumber" fluid />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-if="!isVatProvided" class="flex justify-center items-center my-auto mt-4">
                            <Button :loading="vatSaveLoading" label="Add VAT Details" @click="addVatNumber"
                                severity="primary" />
                        </div>
                    </template>
                </Card>
            </div>
            <!-- Bank details -->
            <div class="col-span-12 md:col-span-12 xl:col-span-9">
                <Card class="h-full card-bg">
                    <template #title>
                        <span class="font-semibold text-lg border-b border-gray-300 ">Bank Details</span>
                    </template>
                    <template #content>
                        <div class="w-full">
                            <div id="name-section" class="w-full md:grid md:grid-cols-3 pt-1 gap-4"
                                style="grid-template-columns: 25% 23% 49%;">

                                <!-- Bank Name -->
                                <div class="flex flex-col w-full">
                                    <span class="font-semibold">Bank Name:</span>
                                    <div class="w-full">
                                        <span v-if="isBankProvided" class="w-full block">{{ user.bank.name }}</span>
                                        <InputText v-else v-model="bankDetails.name" placeholder="Enter Bank Name"
                                            class="pt-1 w-full" fluid />
                                        <span v-if="bankErrors.name" class="error">{{ bankErrors.name }}</span>
                                    </div>
                                </div>

                                <!-- Swift Code -->
                                <div class="flex flex-col w-full">
                                    <span class="font-semibold">Swift Code:</span>
                                    <div class="w-full" style="box-sizing: border-box;">
                                        <span v-if="isBankProvided" class="w-full block">{{ user.bank.swift }}</span>
                                        <InputText v-else v-model="bankDetails.swift" placeholder="Enter Swift Code"
                                            class="pt-1 w-full" fluid />
                                        <span v-if="bankErrors.swift" class="error">{{ bankErrors.swift }}</span>
                                    </div>
                                </div>

                                <!-- IBAN -->
                                <div class="flex flex-col w-full">
                                    <span class="font-semibold">IBAN:</span>
                                    <div class="w-full" style="box-sizing: border-box;">
                                        <span v-if="isBankProvided" class="w-full block">{{ user.bank.iban }}</span>
                                        <InputText v-else v-model="bankDetails.iban" placeholder="Enter IBAN"
                                            class="pt-1 w-full" fluid />
                                        <span v-if="bankErrors.iban" class="error">{{ bankErrors.iban }}</span>
                                    </div>
                                </div>

                            </div>
                        </div>
                        <div v-if="!isBankProvided" class="flex justify-center mt-2">
                            <Button :loading="bankSaveLoading" label="Add Bank Details" @click="addBankDetails"
                                severity="primary" />
                        </div>
                    </template>

                </Card>
            </div>
            <!-- fourth row -->
            <!-- address  -->
            <div class="col-span-12 md:col-span-6 xl:col-span-6">
                <Card class="h-full card-bg">
                    <template #title>
                        <span class="font-semibold text-lg border-b border-gray-300 ">Billing Address</span>
                    </template>
                    <template #content>
                        <div class="w-full">
                            <div id="name-section" class="w-full md:grid md:grid-cols-3 pt-1 gap-4"
                                style="grid-template-columns: 49% 23% 23%;">

                                <!-- street -->
                                <div class="flex flex-col w-full">
                                    <span class="font-semibold">Street name:</span>
                                    <div class="w-full">
                                        <span v-if="isAddressProvided" class="w-full block">{{ user.address.street
                                        }}</span>
                                        <InputText v-else v-model="addressDetails.street"
                                            placeholder="Enter Street name" class="pt-1 w-full" fluid />
                                        <span v-if="adressErrors.street" class="error">{{ adressErrors.street }}</span>
                                    </div>
                                </div>

                                <!-- city -->
                                <div class="flex flex-col w-full">
                                    <span class="font-semibold">City/Town:</span>
                                    <div class="w-full" style="box-sizing: border-box;">
                                        <span v-if="isAddressProvided" class="w-full block">{{ user.address.city
                                        }}</span>
                                        <InputText v-else v-model="addressDetails.city" placeholder="Enter city or town"
                                            class="pt-1 w-full" fluid />
                                        <span v-if="adressErrors.city" class="error">{{ adressErrors.city }}</span>
                                    </div>
                                </div>

                                <!-- country -->
                                <div class="flex flex-col w-full">
                                    <span class="font-semibold">Country:</span>
                                    <div class="w-full" style="box-sizing: border-box;">
                                        <span v-if="isAddressProvided" class="w-full block">{{ user.address.country
                                        }}</span>
                                        <Select v-else v-model="addressDetails.country" filter :options="countries"
                                            :invalid="addressDetails.country === null" optionLabel="name"
                                            placeholder="Select a Country" class="w-full pt-1"
                                            :virtualScrollerOptions="{ itemSize: 30 }">

                                            <!-- Selected Value Template -->
                                            <template #value="slotProps">
                                                <div v-if="slotProps.value" class="flex items-center">
                                                    <span :class="`fi fi-${slotProps.value.code.toLowerCase()}`"
                                                        class="mr-2"></span>
                                                    <div>{{ slotProps.value.name }}</div>
                                                </div>
                                                <span v-else>
                                                    {{ slotProps.placeholder }}
                                                </span>
                                            </template>

                                            <!-- Option Template -->
                                            <template #option="slotProps">
                                                <div class="flex items-center">
                                                    <span :class="`fi fi-${slotProps.option.code.toLowerCase()}`"
                                                        class="mr-2"></span>
                                                    <div>{{ slotProps.option.name }}</div>
                                                </div>
                                            </template>
                                        </Select>
                                        <span v-if="addressDetails.city" class="error">{{ adressErrors.country
                                        }}</span>
                                    </div>
                                </div>

                            </div>
                        </div>
                        <div v-if="!isAddressProvided" class="flex justify-center mt-2">
                            <Button :loading="addressSaveLoading" label="Save Adress Details"
                                @click="saveAdressDetails" />
                        </div>
                    </template>

                </Card>
            </div>
            <!-- change password -->
            <div class="col-span-12 md:col-span-6 xl:col-span-6">
                <Card class="h-full card-bg">
                    <template #title>
                        <span class="font-semibold text-lg border-b border-gray-300 ">Password</span>
                    </template>
                    <template #content>
                        <div class="flex flex-col w-full ">
                            <div v-if="!changePasswordClicked" class="flex justify-center items-center my-auto">
                                <Button :loading="changePasswordLoading" label="Change Password"
                                    @click="showChangePasswordFields" severity="primary" />
                            </div>
                            <div v-else class="flex flex-col md:flex-row w-full gap-4">

                                <!-- Current Password -->
                                <div id="current-pass-section" class="flex flex-col w-full md:w-1/2">
                                    <span class="font-semibold">Current password:</span>
                                    <div class="w-full">
                                        <InputText class="mt-1" v-model="currentPassword"
                                            placeholder="Enter current password" fluid />
                                        <small class="error" v-if="passwordErrors.currentPassword">{{
                                            passwordErrors.currentPassword }}</small>
                                    </div>
                                </div>

                                <!-- New Password -->
                                <div id="new-pass-section" class="flex flex-col w-full md:w-1/2">
                                    <span class="font-semibold">New password:</span>
                                    <div class="w-full">
                                        <InputText class="mt-1" v-model="newPassword" placeholder="Enter new password"
                                            fluid />
                                        <small class="error" v-if="passwordErrors.newPassword">{{
                                            passwordErrors.newPassword }}</small>
                                    </div>
                                </div>

                            </div>
                        </div>

                        <div v-if="changePasswordClicked" class="flex justify-center mt-4">
                            <Button :loading="changePasswordLoading" label="Change Password" @click="changePassword"
                                severity="primary" />
                        </div>
                    </template>
                </Card>
            </div>
            <div class="col-span-12 contact-section">
                <div :class="[
                    'flex flex-col sm:flex-row justify-start items-start gap-8 pt-6 pb-6 transition-all duration-500 cursor-pointer card-bg'
                ]">
<!-- Postbacks -->
<div class="flex items-start gap-3">
    <div class="bg-blue-50 p-2 ml-5">
        <i class="pi pi-server text-blue-500 text-xl"></i>
    </div>
    <div class="flex flex-col gap-3 flex-1">
        <router-link
            :to="{ name: 'PostbackSetupGuide' }"
            class="text-xs text-blue-400 hover:text-blue-300 underline w-fit"
        >
            <i class="pi pi-question-circle mr-1" />How to set up postbacks
        </router-link>
        <!-- View Mode -->
        <div v-if="!isEditingPostbacks && registerPostbackUrl && ftdPostbackUrl" class="flex flex-col gap-2">
            <div class="flex flex-col gap-1">
                <label class="font-semibold text-sm">Global Register Postback URL</label>
                <div class="p-2 ">
                    <code class="text-sm">{{ registerPostbackUrl }}</code>
                </div>
            </div>
            <div class="flex flex-col gap-1">
                <label class="font-semibold text-sm">Global FTD Postback URL</label>
                <div class="p-2  ">
                    <code class="text-sm">{{ ftdPostbackUrl }}</code>
                </div>
            </div>
            <div v-if="cpaPostbackUrl" class="flex flex-col gap-1">
                <label class="font-semibold text-sm">Global CPA Postback URL</label>
                <div class="p-2 ">
                    <code class="text-sm">{{ cpaPostbackUrl }}</code>
                </div>
            </div>
            <Button 
                label="Edit" 
                icon="pi pi-pencil" 
                severity="secondary" 
                outlined
                @click="isEditingPostbacks = true" 
                class="w-auto" 
            />
        </div>
        
        <!-- Edit Mode -->
        <div v-else class="flex flex-col gap-3">
            <div class="flex flex-col gap-1">
                <label class="font-semibold text-sm">Global Register Postback URL</label>
                <InputText 
                    v-model="registerPostbackUrl" 
                    placeholder="Enter register postback URL"
                    class="w-full md:min-w-[600px] lg:min-w-[800px]" 
                />
            </div>
            <div class="flex flex-col gap-1">
                <label class="font-semibold text-sm">Global FTD Postback URL</label>
                <InputText 
                    v-model="ftdPostbackUrl" 
                    placeholder="Enter FTD postback URL"
                    class="w-full md:min-w-[600px] lg:min-w-[800px]" 
                />
            </div>
            <div class="flex flex-col gap-1">
                <label class="font-semibold text-sm">Global CPA Postback URL</label>
                <InputText 
                    v-model="cpaPostbackUrl" 
                    placeholder="Enter CPA postback URL"
                    class="w-full md:min-w-[600px] lg:min-w-[800px]" 
                />
            </div>
            <div class="flex gap-2">
                <Button 
                    label="Save Postbacks" 
                    icon="pi pi-save" 
                    severity="primary"
                    @click="updateGlobalPostbacks" 
                    :loading="savingPostbacks" 
                    class="w-auto" 
                />
                <Button 
                    label="Cancel" 
                    icon="pi pi-times" 
                    severity="secondary" 
                    outlined
                    @click="cancelEditPostbacks" 
                    class="w-auto" 
                />
            </div>
        </div>
    </div>
</div>
                </div>
            </div>
            <!-- Contact Us -->
            <div class="col-span-12 contact-section">
                <div :class="[
                    'flex flex-col sm:flex-row justify-center items-center gap-8 pt-6 pb-6 transition-all duration-500 cursor-pointer',
                    isHighlighted ? 'animate-pulse-custom bg-blue-50 shadow-lg' : 'card-bg'
                ]">
                    <!-- Email -->
                    <div class="flex items-center gap-3">
                        <div class="bg-blue-50 p-2 rounded-full">
                            <i class="pi pi-envelope text-blue-500 text-xl"></i>
                        </div>
                        <div class="flex flex-col">
                            <span class="font-semibold">Email</span>
                            <a :href="contacts.email.href" class="text-blue-500 hover:text-blue-600">
                                {{ contacts.email.value }}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, reactive } from 'vue';
import { useStore } from 'vuex';
import { getFormattedLoggedInDate } from '@/utils/dateHelper';
import SitesService from '@/services/SitesService';
import EarningsService from '@/services/EarningsService';
import UsersService from '@/services/UsersService';
import AuthService from '@/services/AuthService';
import PaymentRequired from '../Invoice/PaymentRequired.vue';
import { useToast } from "primevue/usetoast";
import Utils from '@/utils';
import { useUserStatus } from '@/utils/userStatus';
import countries from '@/utils/countries';
import { getCountryName } from '@/utils/countries';
import { getSiteTypes } from '@/utils/siteTypes';
import { useRoute } from 'vue-router';
import { contacts } from '@/utils/contacts';
import { getRoleName } from '@/utils/roles';
const toast = useToast();

const store = useStore();
const user = computed(() => store.state.user);
const userRole = computed(() => getRoleName(user.value?.roles));
const userId = computed(() => store.state.user ? store.state.user.id : null);

const isLoading = ref(true);
const userMetaData = ref(null);

const vatNumber = ref(null);
const currentPassword = ref('');
const newPassword = ref('');

const changePasswordClicked = ref(false);
// Reactive bank details object
const bankDetails = reactive({
    name: '',
    swift: '',
    iban: ''
});

const addressDetails = reactive({
    street: '',
    city: '',
    country: ''
});

var bankErrors = reactive({
    name: null,
    iban: null,
    swift: null
});

const adressErrors = reactive({
    street: null,
    city: null,
    country: null
});

var passwordErrors = reactive({
    currentPassword: null,
    newPassword: null
})

const showChangePasswordFields = () => changePasswordClicked.value = !changePasswordClicked.value;

const validatePasswordDetails = () => {
    passwordErrors.currentPassword = currentPassword.value.length >= 3 ? null : 'Password must be at least 3 characters long.';
    passwordErrors.newPassword = newPassword.value.length >= 3 ? null : 'Password must be at least 3 characters long.';

    return !passwordErrors.currentPassword && !passwordErrors.newPassword;
}

const validateBankDetails = () => {
    bankErrors.name = bankDetails.name.length >= 3 && /^[a-zA-Z\s]+$/.test(bankDetails.name)
        ? null
        : 'Bank name must be at least 3 characters long and contain only letters and spaces.';

    bankErrors.swift = bankDetails.swift.length > 6
        ? null
        : 'Invalid SWIFT code. It must be greater than 6 characters long.';

    bankErrors.iban = /^[A-Z0-9]{15,34}$/.test(bankDetails.iban)
        ? null
        : 'Invalid IBAN. It must be between 15 and 34 characters.';

    // Return true if there are no errors
    return !bankErrors.name && !bankErrors.swift && !bankErrors.iban;
};

const validateAddressDetails = () => {
    adressErrors.street = addressDetails.street.length >= 3
        ? null
        : 'Enter a valid street name with at least 3 characters';

    adressErrors.city = addressDetails.city.length >= 3
        ? null
        : 'Please enter a valid city/country';

    adressErrors.country = addressDetails.country.code != null
        ? null
        : 'Select a country';

    // Return true if there are no errors
    return !adressErrors.street && !adressErrors.city && !adressErrors.country;
}

const bankSaveLoading = ref(false);
const addressSaveLoading = ref(false);
const vatSaveLoading = ref(false);
const changePasswordLoading = ref(false);
// Computed properties to check if the details are available
const { isBankProvided, isAddressProvided, isVatProvided, showAlert } = useUserStatus(user);

async function addBankDetails() {
    console.log(bankDetails);
    if (validateBankDetails()) {
        bankSaveLoading.value = true;
        try {

            var res = await UsersService.addBankDetails(userId.value, bankDetails);
            console.log(res.data.user);
            store.dispatch('setUser', res.data.user);

        } catch (er) {
            console.error(er);
        } finally {
            bankSaveLoading.value = false;

        }
    }

}

async function saveAdressDetails() {
    console.log(addressDetails);
    if (validateAddressDetails()) {
        addressSaveLoading.value = true;
        try {
            var res = await UsersService
                .addAddressDetails(userId.value,
                    { street: addressDetails.street, city: addressDetails.city, country: addressDetails.country.name });

            console.log(res.data.user);
            store.dispatch('setUser', res.data.user);
        } catch (er) {
            console.error(er);
        } finally {
            addressSaveLoading.value = false;
        }
    }
}

async function addVatNumber() {
    try {
        vatSaveLoading.value = true;
        const response = await UsersService.addVatNumber(userId.value, vatNumber.value);
        console.log('VAT number updated:', response.data);
        // After success, update the user in the Vuex store
        store.dispatch('setUser', response.data.user); // Assuming the response contains the updated user objec
    } catch (e) {
        console.error(e);
    } finally {
        vatSaveLoading.value = false;
    }

}

async function changePassword() {
    if (validatePasswordDetails()) {
        changePasswordLoading.value = true;
        try {
            const response = await AuthService.changePassword(currentPassword.value, newPassword.value);
            console.log(response);
            toast.add({ severity: 'success', summary: `Password changed`, life: 3000 });
            changePasswordClicked.value = false;
        } catch (err) {
            console.error(err);
        } finally {
            changePasswordLoading.value = false;
        }
    }
}

async function fetchSiteCount() {
    try {
        await SitesService.getTotalSitescount(userId.value).then((data) => {
            console.log(data.data);
            userMetaData.value = data.data;
        });
    } catch (err) {
        console.error(err);
    }
}

const totalNetRevenue = ref(null);
async function fetchUserTotalNetRevenue() {
    try {
        await EarningsService.getUserTotalNetRevenue().then((data) => {
            console.log(data.data);
            totalNetRevenue.value = data.data;
        });
    } catch (err) {
        console.error(err);
    }
}

async function fetchUserGlobalPostbacks() {
    try {
        await UsersService.getUserGlobalPostbacks(userId.value).then((data) => {
            console.log(data.data);
            ftdPostbackUrl.value = data.data.globalFtdPostbackUrl;
            registerPostbackUrl.value = data.data.globalRegisterPostbackUrl;
            cpaPostbackUrl.value = data.data.globalCpaPostbackUrl;
        });
    } catch (err) {
        console.error(err);
    }
}

const registerPostbackUrl = ref('');
const ftdPostbackUrl = ref('');
const cpaPostbackUrl = ref('');
const savingPostbacks = ref(false);
const isEditingPostbacks = ref(false);

const cancelEditPostbacks = () => {
    // Reset to original values if needed, or just exit edit mode
    isEditingPostbacks.value = false;
    // Optionally reload the original values here
};
// Update the save function to exit edit mode after saving
const updateGlobalPostbacks = async () => {
    savingPostbacks.value = true;
    try {
        await UsersService.updateGlobalPostbacks(userId.value, {
            ftdPostbackUrl: ftdPostbackUrl.value,
            registerPostbackUrl: registerPostbackUrl.value,
            cpaPostbackUrl: cpaPostbackUrl.value
        });
        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Global postbacks saved successfully',
            life: 3000
        });
        isEditingPostbacks.value = false; // Exit edit mode after successful save
    } catch (error) {
        console.error('Error saving postbacks:', error);
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.message || 'Failed to save postbacks',
            life: 3000
        });
    } finally {
        savingPostbacks.value = false;
    }
};

onMounted(async () => {
    if (userId.value) {
        await fetchSiteCount();
        fetchUserTotalNetRevenue();
        fetchUserGlobalPostbacks();
        const countryObject = countries.value.find(country => country.code === user.value.country);
        addressDetails.country = countryObject;
    }

    isLoading.value = false;
});

//sometimes not loading due to store 
// Watch for changes to userId, trigger fetch when it's available
watch(userId, async (newUserId) => {
    console.log('watcherr');
    if (newUserId) {
        await fetchSiteCount();
        isLoading.value = false;
    }
});

// Create a map for quick lookups
const siteTypeMap = computed(() => {
    return getSiteTypes().reduce((acc, type) => {
        acc[type.code] = type.name;
        return acc;
    }, {});
});

const route = useRoute();
const isHighlighted = ref(false);

const highlightContactSection = () => {
    if (route.query.showHelp === 'true') {
        isHighlighted.value = true;

        // Scroll to contact section
        const element = document.querySelector('.contact-section');
        if (element) {
            // Smooth scroll with a slight delay to ensure rendering
            setTimeout(() => {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'  // Centers the element in the viewport
                });
            }, 100);
        }

        // Remove highlight after 30 seconds
        setTimeout(() => {
            isHighlighted.value = false;
        }, 30000);
    }
};

// Watch for route changes
watch(() => route.query, highlightContactSection);

// Check on initial load
onMounted(highlightContactSection);
</script>

<style>
@keyframes pulseCustom {

    0%,
    100% {
        background-color: rgb(151, 155, 161);
        /* bg-blue-50 */
        box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
    }

    50% {
        background-color: var(--card-background-color);
        box-shadow: none;
    }
}

.animate-pulse-custom {
    animation: pulseCustom 1s ease-in-out infinite;
}
</style>